/* eslint-disable jsx-a11y/media-has-caption */
import { useEffect, useMemo, useRef, useState, Fragment } from 'react';

type AudioPropsType = {
  src: string;
  renderContent: (props: AudioRenderPropsType) => JSX.Element;
};

export type AudioRenderPropsType = {
  error: Error | null;
  duration: number;
  currentTime: number;
  progressPercentage: number;
  isLoading: boolean;
  isBuffering: boolean;
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  stop: () => void;
  toggle: () => void;
  setCurrentTime: (val: number) => void;
};

export const Audio = ({ src, renderContent }: AudioPropsType): JSX.Element => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isBuffering, setIsBuffering] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [_currentTime, _setCurrentTime] = useState<number>(0);

  const duration = (audioRef.current && audioRef.current.duration) || 0;

  const progressPercentage = useMemo(() => {
    return audioRef.current ? (_currentTime / duration) * 100 : 0;
  }, [_currentTime]);

  const onAudioTimeUpdate = () => {
    if (audioRef.current) {
      _setCurrentTime(audioRef.current.currentTime);
    }
  };

  const onAudioCanPlayThrough = () => {
    setIsBuffering(false);
    setIsLoading(false);
    setError(null);
  };

  const handleAudioError = () => {
    setIsLoading(false);
    setError(new Error('There was a problem with the audio playback'));
  };

  const onAudioLoadedMetaData = () => {
    setIsLoading(false);
  };

  const onAudioLoadedData = () => {
    setIsLoading(false);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const onAudioLoadStart = () => {
    setIsLoading(true);
  };

  const onAudioWaiting = () => {
    setIsBuffering(true);
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener('timeupdate', onAudioTimeUpdate);
      audioRef.current.addEventListener('loadedmetadata', onAudioLoadedMetaData);
      audioRef.current.addEventListener('loadeddata', onAudioLoadedData);
      audioRef.current.addEventListener('canplaythrough', onAudioCanPlayThrough);
      audioRef.current.addEventListener('error', handleAudioError);
      audioRef.current.addEventListener('pause', handlePause);
      audioRef.current.addEventListener('play', handlePlay);
      audioRef.current.addEventListener('loadstart', onAudioLoadStart);
      audioRef.current.addEventListener('waiting', onAudioWaiting);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', onAudioTimeUpdate);
        audioRef.current.removeEventListener('loadedmetadata', onAudioLoadedMetaData);
        audioRef.current.removeEventListener('loadeddata', onAudioLoadedData);
        audioRef.current.removeEventListener('canplaythrough', onAudioCanPlayThrough);
        audioRef.current.removeEventListener('error', handleAudioError);
        audioRef.current.removeEventListener('pause', handlePause);
        audioRef.current.removeEventListener('play', handlePlay);
        audioRef.current.removeEventListener('loadstart', onAudioLoadStart);
        audioRef.current.removeEventListener('waiting', onAudioWaiting);
      }
    };
  }, []);

  const play = () => {
    audioRef.current && audioRef.current.play();
  };

  const pause = () => {
    audioRef.current && audioRef.current.pause();
  };

  const stop = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
  };

  const toggle = () => {
    if (!audioRef.current) return;
    !audioRef.current.paused ? audioRef.current.pause() : audioRef.current.play();
  };

  const setCurrentTime = (time: number | undefined) => {
    if (audioRef.current && time) {
      audioRef.current.currentTime = time;
      onAudioTimeUpdate();
    }
  };

  const renderProps: AudioRenderPropsType = {
    play,
    pause,
    stop,
    toggle,
    setCurrentTime,
    error,
    duration,
    isPlaying,
    isLoading,
    isBuffering,
    progressPercentage,
    currentTime: _currentTime,
  };
  return (
    <Fragment>
      <audio src={src} ref={audioRef} />
      {renderContent(renderProps)}
    </Fragment>
  );
};
